import { useEffect, useMemo, useState } from "react";
import {
    ChevronRight,
    LocateFixed,
    MapPin,
    Plus,
    Search,
    X,
} from "lucide-react";
import { MobileSavedAddress } from "./MobileAddressSheetData";
import "./MobileAddressSheet.css";

type MobileAddressSheetProps = {
    open: boolean;
    addresses: MobileSavedAddress[];
    selectedAddress?: string;
    onClose: () => void;
    onSelectAddress: (address: MobileSavedAddress) => void;
    onUseCurrentLocation: () => void;
    onAddNewAddress: () => void;
};

const MobileAddressSheet = ({
    open,
    addresses,
    selectedAddress,
    onClose,
    onSelectAddress,
    onUseCurrentLocation,
    onAddNewAddress,
}: MobileAddressSheetProps) => {
    const [searchValue, setSearchValue] = useState("");

    useEffect(() => {
        if (!open) {
            return;
        }

        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = "hidden";

        const handleEscape = (event: KeyboardEvent) => {
            if (event.key !== "Escape") {
                return;
            }
            onClose();
        };

        window.addEventListener("keydown", handleEscape);

        return () => {
            document.body.style.overflow = previousOverflow;
            window.removeEventListener("keydown", handleEscape);
        };
    }, [onClose, open]);

    useEffect(() => {
        if (!open) {
            setSearchValue("");
        }
    }, [open]);

    const filteredAddresses = useMemo(() => {
        const query = searchValue.trim().toLowerCase();

        if (!query) {
            return addresses;
        }

        return addresses.filter(
            (address) =>
                address.name.toLowerCase().includes(query) ||
                address.tag.toLowerCase().includes(query) ||
                address.address.toLowerCase().includes(query) ||
                address.phone.toLowerCase().includes(query)
        );
    }, [addresses, searchValue]);

    const hasExactSelection = addresses.some(
        (address) => address.address === selectedAddress
    );

    if (!open) {
        return null;
    }

    return (
        <div
            className="salar-mobile-address-layer"
            role="presentation"
            onMouseDown={(event) => {
                if (event.target === event.currentTarget) {
                    onClose();
                }
            }}
        >
            <section
                className="salar-mobile-address-sheet"
                role="dialog"
                aria-modal="true"
                aria-label="Select delivery address"
            >
                <div className="salar-mobile-address-handle" />

                <div className="salar-mobile-address-header">
                    <div>
                        <span>DELIVERY ADDRESS</span>
                        <h2>Select delivery address</h2>
                    </div>

                    <button
                        type="button"
                        className="salar-mobile-address-close"
                        onClick={onClose}
                        aria-label="Close address selector"
                    >
                        <X size={20} strokeWidth={2} />
                    </button>
                </div>

                <div className="salar-mobile-address-body">
                    <label className="salar-mobile-address-search">
                        <Search size={19} strokeWidth={1.9} />

                        <input
                            type="search"
                            value={searchValue}
                            onChange={(event) =>
                                setSearchValue(event.target.value)
                            }
                            placeholder="Search by name, area, street, pincode"
                            aria-label="Search saved addresses"
                        />

                        {searchValue && (
                            <button
                                type="button"
                                onClick={() => setSearchValue("")}
                                aria-label="Clear address search"
                            >
                                <X size={15} strokeWidth={2} />
                            </button>
                        )}
                    </label>

                    <div className="salar-mobile-address-primary-actions">
                        <button
                            type="button"
                            onClick={onUseCurrentLocation}
                        >
                            <span className="salar-mobile-address-action-icon">
                                <LocateFixed size={20} strokeWidth={2} />
                            </span>

                            <span>
                                <strong>Use my current location</strong>
                                <small>Allow access to location</small>
                            </span>

                            <ChevronRight size={18} strokeWidth={2} />
                        </button>

                        <button
                            type="button"
                            onClick={onAddNewAddress}
                        >
                            <span className="salar-mobile-address-action-icon">
                                <Plus size={21} strokeWidth={2.2} />
                            </span>

                            <span>
                                <strong>Add New address</strong>
                                <small>Enter complete delivery details</small>
                            </span>

                            <ChevronRight size={18} strokeWidth={2} />
                        </button>
                    </div>

                    <div className="salar-mobile-address-saved-head">
                        <h3>Saved addresses</h3>
                        <span>{filteredAddresses.length}</span>
                    </div>

                    <div className="salar-mobile-address-list">
                        {filteredAddresses.length > 0 ? (
                            filteredAddresses.map((address) => {
                                const selected =
                                    address.address === selectedAddress ||
                                    (!hasExactSelection &&
                                        Boolean(address.isDefault));

                                return (
                                    <label
                                        className={`salar-mobile-checkout-address-item ${
                                            selected ? "active" : ""
                                        }`}
                                        key={address.id}
                                    >
                                        <input
                                            type="radio"
                                            name="mobileSelectedAddress"
                                            checked={selected}
                                            onChange={() =>
                                                onSelectAddress(address)
                                            }
                                        />

                                        <span className="salar-mobile-checkout-address-content">
                                            <span className="salar-mobile-checkout-address-topline">
                                                <span className="salar-mobile-checkout-address-tag">
                                                    {address.tag}
                                                </span>
                                                <strong>{address.name}</strong>
                                                <span>{address.phone}</span>
                                            </span>

                                            <span className="salar-mobile-checkout-address-text">
                                                {address.address}
                                            </span>
                                        </span>
                                    </label>
                                );
                            })
                        ) : (
                            <div className="salar-mobile-address-empty">
                                <MapPin size={25} strokeWidth={1.8} />

                                <strong>No saved address found</strong>

                                <span>
                                    Try another name, area, street or pincode.
                                </span>
                            </div>
                        )}
                    </div>
                </div>
            </section>
        </div>
    );
};

export default MobileAddressSheet;
